/**
 * API Helper Utilities
 * Provides common utilities for making authenticated API calls
 */

/**
 * Get authentication headers with JWT token
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Get API base URL (development vs production)
 */
export const getApiBaseUrl = (): string => {
  // Check for environment variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Default to localhost for development
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001/api';
  }

  // Production fallback
  return 'https://roommate-finder-backend-production.up.railway.app/api';
};

/**
 * Get WebSocket URL for Socket.io
 */
export const getWebSocketUrl = (): string => {
  // Check for environment variable first
  if (process.env.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }

  // Default to localhost for development
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001';
  }

  // Production fallback (same as API base URL without /api)
  const apiUrl = getApiBaseUrl();
  return apiUrl.replace('/api', '');
};

/**
 * Handle API response errors
 */
export const handleApiError = (error: any): { message: string; errors?: any[] } => {
  if (error instanceof Error) {
    return {
      message: error.message || 'An unexpected error occurred',
      errors: [error],
    };
  }

  if (error?.response) {
    // Axios-style error
    const status = error.response.status;
    const data = error.response.data;

    if (status === 401) {
      return {
        message: 'Authentication failed. Please log in again.',
        errors: [error],
      };
    }

    if (status === 403) {
      return {
        message: 'You do not have permission to perform this action.',
        errors: [error],
      };
    }

    if (status === 404) {
      return {
        message: 'Resource not found.',
        errors: [error],
      };
    }

    if (status >= 500) {
      return {
        message: 'Server error. Please try again later.',
        errors: [error],
      };
    }

    return {
      message: data?.message || `Request failed with status ${status}`,
      errors: [error],
    };
  }

  if (error?.message) {
    return {
      message: error.message,
      errors: [error],
    };
  }

  return {
    message: 'An unexpected error occurred',
    errors: [error],
  };
};

/**
 * Make authenticated API request with retry logic
 */
export const makeApiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {},
  retryOptions?: { maxRetries?: number; retryDelay?: number; retryableStatuses?: number[] }
): Promise<{ success: boolean; data?: T; message: string; errors?: any[] }> => {
  const baseURL = getApiBaseUrl();
  const url = endpoint.startsWith('/') 
    ? `${baseURL}${endpoint}` 
    : `${baseURL}/${endpoint}`;

  const headers = getAuthHeaders();
  const maxRetries = retryOptions?.maxRetries ?? 2;
  const retryDelay = retryOptions?.retryDelay ?? 1000;
  const retryableStatuses = retryOptions?.retryableStatuses ?? [408, 429, 500, 502, 503, 504];
  let lastError: any = null;

  // Check if offline
  if (!navigator.onLine) {
    return {
      success: false,
      message: 'You are currently offline. Please check your internet connection.',
      errors: [new Error('Network offline')],
    };
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers || {}),
        },
      });

      // Handle non-JSON responses
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text ? { message: text } : {};
      }

      // Handle authentication errors (don't retry)
      if (response.status === 401) {
        // Clear invalid token
        localStorage.removeItem('token');
        return {
          success: false,
          message: 'Your session has expired. Please log in again.',
          errors: [{ status: 401, message: 'Unauthorized' }],
        };
      }

      // Handle forbidden errors (don't retry)
      if (response.status === 403) {
        return {
          success: false,
          message: 'You do not have permission to perform this action.',
          errors: [{ status: 403, message: 'Forbidden' }],
        };
      }

      // Handle not found errors (don't retry)
      if (response.status === 404) {
        return {
          success: false,
          message: data.message || 'Resource not found.',
          errors: [{ status: 404, message: 'Not Found' }],
        };
      }

      // Retry on retryable status codes
      if (!response.ok && retryableStatuses.includes(response.status) && attempt < maxRetries) {
        lastError = {
          status: response.status,
          message: data.message || `Request failed with status ${response.status}`,
        };
        await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        continue;
      }

      if (!response.ok) {
        return {
          success: false,
          message: data.message || `Request failed with status ${response.status}`,
          errors: data.errors || [{ status: response.status, message: data.message }],
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Request successful',
      };
    } catch (error) {
      lastError = error;

      // Don't retry on network errors if we're offline
      if (error instanceof TypeError && error.message.includes('fetch')) {
        if (!navigator.onLine) {
          return {
            success: false,
            message: 'You are currently offline. Please check your internet connection.',
            errors: [error],
          };
        }

        // Retry network errors
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
          continue;
        }
      }

      // Don't retry on other errors
      const errorInfo = handleApiError(error);
      return {
        success: false,
        message: errorInfo.message,
        errors: errorInfo.errors,
      };
    }
  }

  // All retries exhausted
  const errorInfo = handleApiError(lastError);
  return {
    success: false,
    message: errorInfo.message || 'Request failed after multiple attempts. Please try again later.',
    errors: errorInfo.errors || [lastError],
  };
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

